import type { PaymentItem } from '@/checkout/checkout.service';
import { takeReduxPersisted } from '@/lib/takeReduxPersisted';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import type { Payment } from '@whiz-cart/node-shared/models/payment/payment';
import PaymentStatus from '@whiz-cart/node-shared/paymentStatus';
import { createStore, persist } from 'cross-state';
import _ from 'lodash';

export type ExpressSnapshot = {
    storeGuid: string;
    stationId: string;
    cashierId: string;
    deviceId: string;
    sessionGuid: string;
    paymentRequestGuid: string;
    imageUrl: string;
    weight: number;
};

export interface CheckoutState {
    payments: PaymentItem[];
    hasLoadedOnce: boolean;
    loading: boolean;
    expressCheckout: (ExpressSnapshot & { date: Date })[];
    cashierGuid: string | undefined;
}

export const checkoutStore = createStore<CheckoutState>({
    payments: [],
    hasLoadedOnce: false,
    loading: true,
    expressCheckout: [],
    cashierGuid: undefined,
});

persist(checkoutStore, { id: 'checkout', storage: localStorage, paths: ['cashierGuid'] });

const legacy = takeReduxPersisted('checkout');
if (typeof legacy?.cashierGuid === 'string') {
    checkoutStore.set('cashierGuid', legacy.cashierGuid);
}

// Finished payments are kept for a day after their last update, to show e.g. that they were cancelled
const retention = 24 * 60 * 60 * 1000;
const markUpdatedOn = (payment: PaymentItem): PaymentItem => ({ ...payment, updatedOn: new Date() });
const filterActive = (payment: PaymentItem) =>
    (typeof payment.status === 'number' && payment.status < PaymentStatus.Completed) ||
    (payment.updatedOn?.getTime() ?? 0) >= Date.now() - retention;

const setPayments = (update: (payments: PaymentItem[]) => PaymentItem[]) => checkoutStore.set('payments', update);

const updateOne = (paymentRequestGuid: string, update: (payment: PaymentItem) => PaymentItem) =>
    setPayments((payments) => payments.map((payment) => (payment.paymentRequestGuid === paymentRequestGuid ? update(payment) : payment)));

const patchPayment = (paymentRequestGuid: string, patch: Partial<PaymentItem>) =>
    setPayments((payments) =>
        payments
            .map((payment) => (payment.paymentRequestGuid === paymentRequestGuid ? { ...payment, ...patch } : payment))
            .filter(filterActive),
    );

export const updatePayments = (payments: Payment[]) => setPayments(() => (payments as PaymentItem[]).map(markUpdatedOn));

export const updatePayment = (payment: Payment) =>
    setPayments((payments) =>
        payments
            .filter((p) => p.paymentRequestGuid !== payment.paymentRequestGuid)
            .concat(markUpdatedOn(payment as PaymentItem))
            .filter(filterActive),
    );

type Lock = { paymentRequestGuid: string; userId: string; deviceId: string };

export const lockPayment = ({ paymentRequestGuid, userId, deviceId }: Lock) =>
    patchPayment(paymentRequestGuid, { processingBy: { userId, deviceId } });

export const unlockPayment = ({ paymentRequestGuid }: { paymentRequestGuid: string }) =>
    patchPayment(paymentRequestGuid, { processingBy: null });

export const lockPosProcessingPayment = ({ paymentRequestGuid, userId, deviceId }: Lock) =>
    patchPayment(paymentRequestGuid, { postProcessingBy: { userId, deviceId } });

export const unlockPosProcessingPayment = ({ paymentRequestGuid }: { paymentRequestGuid: string }) =>
    patchPayment(paymentRequestGuid, { postProcessingBy: null });

export const cancelPayment = ({ paymentRequestGuid }: { paymentRequestGuid: string }) =>
    patchPayment(paymentRequestGuid, { status: PaymentStatus.EmployeeCancelled });

export const completePayment = ({
    paymentRequestGuid,
    posRegistrationCompletedOn,
}: {
    paymentRequestGuid: string;
    posRegistrationCompletedOn: PaymentItem['posRegistrationCompletedOn'];
}) => patchPayment(paymentRequestGuid, { status: PaymentStatus.Completed, posRegistrationCompletedOn });

export const verifyPayment = ({
    paymentRequestGuid,
    verificationGuid,
    approved,
}: {
    paymentRequestGuid: string;
    verificationGuid: string;
    approved: boolean;
}) =>
    updateOne(paymentRequestGuid, (payment) => ({
        ...payment,
        verificationItems: payment.verificationItems?.map((item) =>
            item.verificationItemGuid === verificationGuid ? { ...item, approved } : item,
        ),
    }));

export const setPaymentLoading = (loading: boolean) =>
    checkoutStore.set((state) => ({ ...state, hasLoadedOnce: state.hasLoadedOnce || !loading, loading }));

export const addExpressSnapshot = (snapshot: ExpressSnapshot) =>
    checkoutStore.set('expressCheckout', (expressCheckout) =>
        _.uniqBy(_.orderBy([...expressCheckout, { ...snapshot, date: new Date() }], [(x) => x.date], ['desc']), (x) => x.cashierId),
    );

export const clearExpressSnapshots = () => checkoutStore.set('expressCheckout', []);

export const setCashierGuid = (cashierGuid: string) => checkoutStore.set('cashierGuid', cashierGuid);

export const updateCustomerLabels = (customerGuid: string, customerLabels: CustomerLabel[]) => {
    const labels = customerLabels.filter((label) => !label.deletedOn).map((label) => label.label);

    setPayments((payments) =>
        payments.map((payment) =>
            payment.customer?.userGuid === customerGuid ? { ...payment, customer: { ...payment.customer, labels } } : payment,
        ),
    );
};
